// =====================================================
// AbschlussReport.jsx — Tagesabschluss & Reports
// Verkaufserfassung · Hardware-Incentives · Auswertung
// =====================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';

function fmtEuro(v) { return (v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }
function generateId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
function toDateKey(d) { return d.toISOString().split('T')[0]; }

const STORAGE_KEY = 'tef-abschluss-v1';

function GlassCard({ children, glow = 'cyan', style = {} }) {
  const glowMap = {
    cyan:   { border: 'rgba(56,189,248,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(56,189,248,0.08)' },
    green:  { border: 'rgba(0,245,212,0.18)',   bg: 'rgba(10,25,50,0.55)',  shadow: 'rgba(0,245,212,0.08)' },
    pink:   { border: 'rgba(255,102,162,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(255,102,162,0.08)' },
    purple: { border: 'rgba(167,139,250,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(167,139,250,0.08)' },
    orange: { border: 'rgba(251,146,60,0.18)',  bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(251,146,60,0.08)' },
  };
  const c = glowMap[glow] || glowMap.cyan;
  return (
    <div style={{ borderRadius: 20, border: `1px solid ${c.border}`, background: c.bg, backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', boxShadow: `0 8px 32px ${c.shadow}, inset 0 1px 0 rgba(255,255,255,0.06)`, padding: '18px 16px', ...style }}>
      {children}
    </div>
  );
}

function SectionLabel({ children, icon }) {
  return (
    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'rgba(200,220,255,0.82)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon && <span>{icon}</span>}{children}
    </div>
  );
}

// Sale Categories
const SALE_CATEGORIES = [
  { id: 'tarif-neu', label: '📶 Neuvertrag', color: '#38bdf8' },
  { id: 'tarif-vvl', label: '🔄 Vertragsverlängerung', color: '#00f5d4' },
  { id: 'tarif-mnp', label: '📲 Rufnummermitnahme', color: '#a78bfa' },
  { id: 'hardware', label: '📱 Hardware', color: '#fb923c' },
  { id: 'zubehoer', label: '🛍️ Zubehör', color: '#ff66a2' },
  { id: 'router', label: '📡 Router', color: '#fbbf24' },
  { id: 'sonstiges', label: '📦 Sonstiges', color: '#94a3b8' },
];

// Demo/Test Data Generator
function generateDemoData() {
  const today = new Date();
  const entries = [];
  const descriptions = {
    'tarif-neu': [
      { desc: 'o2 Mobile M Boost', device: 'Samsung Galaxy S25', revenue: 39.99, prov: 180 },
      { desc: 'o2 Mobile L', device: 'iPhone 16 Pro', revenue: 49.99, prov: 250 },
      { desc: 'o2 Mobile S', device: 'Samsung Galaxy A56', revenue: 24.99, prov: 120 },
      { desc: 'o2 Mobile M', device: 'iPhone 16', revenue: 39.99, prov: 200 },
      { desc: 'o2 Mobile XL', device: 'Google Pixel 9 Pro', revenue: 59.99, prov: 300 },
    ],
    'tarif-vvl': [
      { desc: 'VVL o2 Mobile M', device: 'Samsung Galaxy S25 Ultra', revenue: 39.99, prov: 150 },
      { desc: 'VVL o2 Mobile L', device: 'iPhone 15', revenue: 49.99, prov: 200 },
      { desc: 'VVL o2 Mobile S', device: '', revenue: 24.99, prov: 80 },
    ],
    'tarif-mnp': [
      { desc: 'MNP o2 Mobile M Boost', device: 'iPhone 16 Pro Max', revenue: 39.99, prov: 280 },
      { desc: 'MNP o2 Mobile L', device: 'Samsung Galaxy Z Fold6', revenue: 49.99, prov: 320 },
    ],
    'hardware': [
      { desc: 'Samsung Galaxy S25 Ultra', device: 'Samsung Galaxy S25 Ultra', revenue: 1449.00, prov: 45 },
      { desc: 'iPhone 16 Pro Max 256GB', device: 'iPhone 16 Pro Max', revenue: 1449.00, prov: 50 },
      { desc: 'Samsung Galaxy A56 5G', device: 'Samsung Galaxy A56', revenue: 479.00, prov: 25 },
      { desc: 'Google Pixel 9', device: 'Google Pixel 9', revenue: 899.00, prov: 35 },
    ],
    'zubehoer': [
      { desc: 'Samsung Clear Case S25', device: '', revenue: 29.99, prov: 8 },
      { desc: 'Apple MagSafe Ladegerät', device: '', revenue: 49.00, prov: 12 },
      { desc: 'Panzer-Displayschutz', device: '', revenue: 24.99, prov: 10 },
    ],
    'router': [
      { desc: 'FRITZ!Box 7690', device: 'o2 HomeBox 7690', revenue: 0, prov: 30 },
      { desc: 'FRITZ!Repeater 6000', device: 'FRITZ!Repeater 6000', revenue: 219.99, prov: 20 },
    ],
    'sonstiges': [
      { desc: 'Sim-Karten Tausch', device: '', revenue: 0, prov: 0 },
      { desc: 'Tarifberatung', device: '', revenue: 0, prov: 0 },
    ],
  };
  // Generate 20-30 entries spread across the current month
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  for (let i = 0; i < 25; i++) {
    const cats = Object.keys(descriptions);
    const cat = cats[Math.floor(Math.random() * cats.length)];
    const items = descriptions[cat];
    const item = items[Math.floor(Math.random() * items.length)];
    const day = Math.min(today.getDate(), Math.floor(Math.random() * daysInMonth) + 1);
    const date = new Date(today.getFullYear(), today.getMonth(), day);
    entries.push({
      id: generateId(),
      date: toDateKey(date),
      timestamp: date.toISOString(),
      category: cat,
      description: item.desc,
      revenue: item.revenue,
      provision: item.prov,
      deviceName: item.device,
      refunded: cat === 'hardware' && Math.random() < 0.15,
    });
  }
  // Ensure at least 5 entries for today
  for (let i = 0; i < 5; i++) {
    const cats = ['tarif-neu', 'tarif-vvl', 'tarif-mnp', 'hardware', 'zubehoer'];
    const cat = cats[i % cats.length];
    const items = descriptions[cat];
    const item = items[Math.floor(Math.random() * items.length)];
    entries.push({
      id: generateId(),
      date: toDateKey(today),
      timestamp: today.toISOString(),
      category: cat,
      description: item.desc,
      revenue: item.revenue,
      provision: item.prov,
      deviceName: item.device,
      refunded: false,
    });
  }
  return entries;
}

const PRINT_CSS = `
@media print {
  body > *:not(.abschluss-print) { display: none !important; }
  .abschluss-print { position: static !important; background: #fff !important; color: #000 !important; }
  .abschluss-print * { color: #000 !important; background: transparent !important; border-color: #ccc !important; }
  .no-print { display: none !important; }
}
`;

// Hardware Incentive Tiers
const INCENTIVE_TIERS = [
  { min: 0, max: 4, label: 'Bronze', bonus: 0, color: '#cd7f32', icon: '🥉' },
  { min: 5, max: 9, label: 'Silber', bonus: 50, color: '#c0c0c0', icon: '🥈' },
  { min: 10, max: 14, label: 'Gold', bonus: 100, color: '#ffd700', icon: '🥇' },
  { min: 15, max: 999, label: 'Platin', bonus: 200, color: '#e5e4e2', icon: '💎' },
];

export function AbschlussReport() {
  const [entries, setEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('today');
  const [selectedDate, setSelectedDate] = useState(new Date());

  // New Entry Form
  const [newCategory, setNewCategory] = useState('tarif-neu');
  const [newDescription, setNewDescription] = useState('');
  const [newRevenue, setNewRevenue] = useState('');
  const [newProvision, setNewProvision] = useState('');
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newRefunded, setNewRefunded] = useState(false);
  const [entryViewMode, setEntryViewMode] = useState('list');

  // Print CSS injection
  useEffect(() => {
    if (!document.getElementById('abschluss-print-css')) {
      const s = document.createElement('style');
      s.id = 'abschluss-print-css';
      s.textContent = PRINT_CSS;
      document.head.appendChild(s);
    }
  }, []);

  // Load demo data
  const loadDemoData = useCallback(() => {
    const demo = generateDemoData();
    saveEntries(demo);
  }, []);

  // Print report
  const handlePrint = useCallback(() => { window.print(); }, []);

  // Load entries
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      setEntries(saved);
    } catch { setEntries([]); }
  }, []);

  const saveEntries = useCallback((updated) => {
    setEntries(updated);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch {}
  }, []);

  const addEntry = useCallback(() => {
    if (!newDescription.trim() && !newDeviceName.trim()) return;
    const entry = {
      id: generateId(),
      date: toDateKey(selectedDate),
      timestamp: new Date().toISOString(),
      category: newCategory,
      description: newDescription || newDeviceName,
      revenue: parseFloat(newRevenue) || 0,
      provision: parseFloat(newProvision) || 0,
      deviceName: newDeviceName,
      refunded: newRefunded,
    };
    const updated = [entry, ...entries];
    saveEntries(updated);
    setNewDescription(''); setNewRevenue(''); setNewProvision(''); setNewDeviceName(''); setNewRefunded(false);
  }, [entries, newCategory, newDescription, newRevenue, newProvision, newDeviceName, newRefunded, selectedDate, saveEntries]);

  const deleteEntry = useCallback((id) => {
    saveEntries(entries.filter(e => e.id !== id));
  }, [entries, saveEntries]);

  const toggleRefunded = useCallback((id) => {
    const updated = entries.map(e => e.id === id ? { ...e, refunded: !e.refunded } : e);
    saveEntries(updated);
  }, [entries, saveEntries]);

  // Today's entries
  const todayKey = toDateKey(selectedDate);
  const todayEntries = useMemo(() => entries.filter(e => e.date === todayKey), [entries, todayKey]);

  // Stats
  const stats = useMemo(() => {
    const today = todayEntries;
    const totalRevenue = today.reduce((s, e) => s + e.revenue, 0);
    const totalProvision = today.reduce((s, e) => s + e.provision, 0);
    const byCat = {};
    SALE_CATEGORIES.forEach(c => { byCat[c.id] = { count: 0, revenue: 0, provision: 0 }; });
    today.forEach(e => {
      if (byCat[e.category]) {
        byCat[e.category].count++;
        byCat[e.category].revenue += e.revenue;
        byCat[e.category].provision += e.provision;
      }
    });
    const hardwareCount = (byCat['hardware']?.count || 0) + (byCat['router']?.count || 0);
    const refundedCount = today.filter(e => e.refunded).length;
    return { totalRevenue, totalProvision, byCat, hardwareCount, refundedCount, totalCount: today.length };
  }, [todayEntries]);

  // Monthly Stats
  const monthStats = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const monthEntries = entries.filter(e => {
      const d = new Date(e.date);
      return d.getFullYear() === year && d.getMonth() === month;
    });
    const revenue = monthEntries.reduce((s, e) => s + e.revenue, 0);
    const provision = monthEntries.reduce((s, e) => s + e.provision, 0);
    const hw = monthEntries.filter(e => e.category === 'hardware' || e.category === 'router').length;
    const tier = INCENTIVE_TIERS.find(t => hw >= t.min && hw <= t.max) || INCENTIVE_TIERS[0];
    return { revenue, provision, hardwareCount: hw, tier, entryCount: monthEntries.length };
  }, [entries, selectedDate]);

  const currentTier = INCENTIVE_TIERS.find(t => monthStats.hardwareCount >= t.min && monthStats.hardwareCount <= t.max) || INCENTIVE_TIERS[0];
  const nextTier = INCENTIVE_TIERS.find(t => t.min > monthStats.hardwareCount);

  const tabs = [
    { id: 'today', label: '📅 Tageserfassung' },
    { id: 'report', label: '📊 Auswertung' },
    { id: 'incentive', label: '🏆 Incentives' },
  ];

  return (
    <div className="abschluss-print" style={{ minHeight: '100vh', color: '#fff', fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 100px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, background: 'linear-gradient(135deg, #ff66a2, #a78bfa, #38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>📊 Tagesabschluss</div>
          <div style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.72)', marginTop: 4 }}>Verkäufe erfassen · Auswertungen · Hardware-Incentives</div>
        </div>

        {/* Action Bar */}
        <div className="no-print" style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 10 }}>
          <button onClick={loadDemoData}
            style={{ padding: '6px 14px', borderRadius: 10, border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.06)', color: '#a78bfa', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            🎲 Demo-Daten laden
          </button>
          <button onClick={handlePrint}
            style={{ padding: '6px 14px', borderRadius: 10, border: '1px solid rgba(0,245,212,0.3)', background: 'rgba(0,245,212,0.06)', color: '#00f5d4', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            🖨️ Report drucken
          </button>
          <button onClick={() => { if (confirm('Alle Einträge löschen?')) saveEntries([]); }}
            style={{ padding: '6px 14px', borderRadius: 10, border: '1px solid rgba(255,0,110,0.3)', background: 'rgba(255,0,110,0.06)', color: '#ff006e', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            🗑️ Alle löschen
          </button>
        </div>

        {/* Date Selector */}
        <GlassCard glow="cyan" style={{ marginBottom: 12, padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); setSelectedDate(d); }}
              style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '1.2rem', cursor: 'pointer' }}>◀</button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#fff' }}>{selectedDate.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
              <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.6)' }}>{todayEntries.length} Einträge</div>
            </div>
            <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); setSelectedDate(d); }}
              style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '1.2rem', cursor: 'pointer' }}>▶</button>
            <button onClick={() => setSelectedDate(new Date())}
              style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(56,189,248,0.2)', background: 'rgba(56,189,248,0.06)', color: '#38bdf8', fontSize: '0.55rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Heute</button>
          </div>
        </GlassCard>

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 4, border: '1px solid rgba(255,255,255,0.06)' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ flex: 1, padding: '10px 8px', borderRadius: 10, border: 'none', background: activeTab === tab.id ? 'rgba(56,189,248,0.15)' : 'transparent', color: activeTab === tab.id ? '#38bdf8' : 'rgba(200,220,255,0.65)', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ========== TODAY TAB ========== */}
        {activeTab === 'today' && (<>
          {/* Quick Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
            <GlassCard glow="green" style={{ padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#00f5d4' }}>{stats.totalCount}</div>
              <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.65)', fontWeight: 700, textTransform: 'uppercase' }}>Verkäufe</div>
            </GlassCard>
            <GlassCard glow="cyan" style={{ padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#38bdf8' }}>{fmtEuro(stats.totalRevenue)}</div>
              <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.65)', fontWeight: 700, textTransform: 'uppercase' }}>Umsatz</div>
            </GlassCard>
            <GlassCard glow="purple" style={{ padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#a78bfa' }}>{fmtEuro(stats.totalProvision)}</div>
              <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.65)', fontWeight: 700, textTransform: 'uppercase' }}>Provision</div>
            </GlassCard>
          </div>

          {/* Add Entry Form */}
          <GlassCard glow="cyan" style={{ marginBottom: 14 }}>
            <SectionLabel icon="➕">Neuer Eintrag</SectionLabel>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
              {SALE_CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => setNewCategory(cat.id)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${newCategory === cat.id ? cat.color + '66' : 'rgba(255,255,255,0.06)'}`, background: newCategory === cat.id ? cat.color + '18' : 'rgba(255,255,255,0.02)', color: newCategory === cat.id ? cat.color : 'rgba(200,220,255,0.72)', fontSize: '0.58rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {cat.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 3, textTransform: 'uppercase' }}>Beschreibung</div>
                <input value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder="z.B. o2 Mobile M"
                  style={{ width: '100%', padding: '9px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '0.75rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 3, textTransform: 'uppercase' }}>Gerätename</div>
                <input value={newDeviceName} onChange={e => setNewDeviceName(e.target.value)} placeholder="z.B. iPhone 16"
                  style={{ width: '100%', padding: '9px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '0.75rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <div>
                <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 3, textTransform: 'uppercase' }}>Umsatz (€)</div>
                <input type="number" value={newRevenue} onChange={e => setNewRevenue(e.target.value)} placeholder="0.00"
                  style={{ width: '100%', padding: '9px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '0.75rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 3, textTransform: 'uppercase' }}>Provision (€)</div>
                <input type="number" value={newProvision} onChange={e => setNewProvision(e.target.value)} placeholder="0.00"
                  style={{ width: '100%', padding: '9px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '0.75rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <button onClick={addEntry}
                style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #38bdf8, #0284c7)', color: '#fff', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(56,189,248,0.3)' }}>
                ➕
              </button>
            </div>
          </GlassCard>

          {/* Entry List / Grid */}
          <GlassCard glow="purple" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <SectionLabel icon="📋">Einträge ({todayEntries.length})</SectionLabel>
              <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3, border: '1px solid rgba(255,255,255,0.06)' }}>
                <button onClick={() => setEntryViewMode('grid')}
                  style={{ padding: '5px 10px', borderRadius: 7, border: 'none', background: entryViewMode === 'grid' ? 'rgba(167,139,250,0.15)' : 'transparent', color: entryViewMode === 'grid' ? '#a78bfa' : 'rgba(200,220,255,0.5)', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.2s' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  Kacheln
                </button>
                <button onClick={() => setEntryViewMode('list')}
                  style={{ padding: '5px 10px', borderRadius: 7, border: 'none', background: entryViewMode === 'list' ? 'rgba(167,139,250,0.15)' : 'transparent', color: entryViewMode === 'list' ? '#a78bfa' : 'rgba(200,220,255,0.5)', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.2s' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Liste
                </button>
              </div>
            </div>
            {todayEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(200,220,255,0.5)', fontSize: '0.72rem' }}>Noch keine Einträge für diesen Tag</div>
            ) : entryViewMode === 'list' ? (
              /* List View */
              todayEntries.map(entry => {
                const cat = SALE_CATEGORIES.find(c => c.id === entry.category) || SALE_CATEGORIES[6];
                return (
                  <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: entry.refunded ? 'rgba(0,245,212,0.04)' : 'rgba(255,255,255,0.02)', border: `1px solid ${entry.refunded ? 'rgba(0,245,212,0.15)' : 'rgba(255,255,255,0.05)'}`, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 6, background: cat.color + '18', color: cat.color, fontSize: '0.52rem', fontWeight: 700 }}>{cat.label}</span>
                        {entry.refunded && <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(0,245,212,0.1)', color: '#00f5d4', fontSize: '0.46rem', fontWeight: 700 }}>✓ Erstattet</span>}
                      </div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', marginTop: 4 }}>{entry.description}</div>
                      {entry.deviceName && <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.6)' }}>📱 {entry.deviceName}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(entry.revenue)}</div>
                      {entry.provision > 0 && <div style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.6)' }}>Prov. {fmtEuro(entry.provision)}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(entry.category === 'hardware' || entry.category === 'router') && (
                        <button onClick={() => toggleRefunded(entry.id)}
                          style={{ padding: '3px 8px', borderRadius: 6, border: `1px solid ${entry.refunded ? 'rgba(0,245,212,0.3)' : 'rgba(255,255,255,0.1)'}`, background: entry.refunded ? 'rgba(0,245,212,0.08)' : 'rgba(255,255,255,0.03)', color: entry.refunded ? '#00f5d4' : 'rgba(200,220,255,0.6)', fontSize: '0.46rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {entry.refunded ? '✓' : '○'}
                        </button>
                      )}
                      <button onClick={() => deleteEntry(entry.id)}
                        style={{ background: 'none', border: 'none', color: '#ff006e', cursor: 'pointer', fontSize: '0.72rem', padding: '2px' }}>✕</button>
                    </div>
                  </div>
                );
              })
            ) : (
              /* Grid View */
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {todayEntries.map(entry => {
                  const cat = SALE_CATEGORIES.find(c => c.id === entry.category) || SALE_CATEGORIES[6];
                  return (
                    <div key={entry.id} style={{ padding: '14px 14px', borderRadius: 14, background: entry.refunded ? 'rgba(0,245,212,0.04)' : 'rgba(255,255,255,0.02)', border: `1px solid ${entry.refunded ? 'rgba(0,245,212,0.15)' : 'rgba(255,255,255,0.05)'}`, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 8, background: cat.color + '18', color: cat.color, fontSize: '0.52rem', fontWeight: 700 }}>{cat.label}</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {(entry.category === 'hardware' || entry.category === 'router') && (
                            <button onClick={() => toggleRefunded(entry.id)}
                              style={{ padding: '3px 8px', borderRadius: 6, border: `1px solid ${entry.refunded ? 'rgba(0,245,212,0.3)' : 'rgba(255,255,255,0.1)'}`, background: entry.refunded ? 'rgba(0,245,212,0.08)' : 'rgba(255,255,255,0.03)', color: entry.refunded ? '#00f5d4' : 'rgba(200,220,255,0.6)', fontSize: '0.46rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                              {entry.refunded ? '✓' : '○'}
                            </button>
                          )}
                          <button onClick={() => deleteEntry(entry.id)}
                            style={{ background: 'none', border: 'none', color: '#ff006e', cursor: 'pointer', fontSize: '0.72rem', padding: '2px' }}>✕</button>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff' }}>{entry.description}</div>
                        {entry.deviceName && <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.6)', marginTop: 2 }}>📱 {entry.deviceName}</div>}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 'auto' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 900, color: '#00f5d4' }}>{fmtEuro(entry.revenue)}</div>
                        {entry.provision > 0 && <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.6)' }}>Prov. {fmtEuro(entry.provision)}</div>}
                      </div>
                      {entry.refunded && <div style={{ position: 'absolute', top: 8, right: 8, padding: '2px 6px', borderRadius: 4, background: 'rgba(0,245,212,0.1)', color: '#00f5d4', fontSize: '0.42rem', fontWeight: 700 }}>✓ Erstattet</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        </>)}

        {/* ========== REPORT TAB ========== */}
        {activeTab === 'report' && (
          <GlassCard glow="cyan">
            <SectionLabel icon="📊">Monatsauswertung — {selectedDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 }}>
              <div style={{ padding: '14px 12px', borderRadius: 14, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#38bdf8' }}>{monthStats.entryCount}</div>
                <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.65)', fontWeight: 700, textTransform: 'uppercase' }}>Verkäufe</div>
              </div>
              <div style={{ padding: '14px 12px', borderRadius: 14, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#00f5d4' }}>{fmtEuro(monthStats.revenue)}</div>
                <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.65)', fontWeight: 700, textTransform: 'uppercase' }}>Umsatz</div>
              </div>
              <div style={{ padding: '14px 12px', borderRadius: 14, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#a78bfa' }}>{fmtEuro(monthStats.provision)}</div>
                <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.65)', fontWeight: 700, textTransform: 'uppercase' }}>Provision</div>
              </div>
              <div style={{ padding: '14px 12px', borderRadius: 14, background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#fb923c' }}>{monthStats.hardwareCount}</div>
                <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.65)', fontWeight: 700, textTransform: 'uppercase' }}>Hardware verkauft</div>
              </div>
            </div>
            {/* Category Breakdown */}
            <div style={{ fontSize: '0.6rem', fontWeight: 800, color: 'rgba(200,220,255,0.8)', marginBottom: 8, textTransform: 'uppercase' }}>Kategorien (Heute)</div>
            {SALE_CATEGORIES.map(cat => {
              const data = stats.byCat[cat.id] || { count: 0, revenue: 0 };
              if (data.count === 0) return null;
              return (
                <div key={cat.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', marginBottom: 4, border: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: cat.color, fontSize: '0.68rem', fontWeight: 700 }}>{cat.label}</span>
                    <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.04)', fontSize: '0.52rem', color: 'rgba(200,220,255,0.72)' }}>{data.count}x</span>
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(data.revenue)}</span>
                </div>
              );
            })}
          </GlassCard>
        )}

        {/* ========== INCENTIVE TAB ========== */}
        {activeTab === 'incentive' && (
          <div>
            <GlassCard glow="orange" style={{ marginBottom: 14, background: `linear-gradient(135deg, ${currentTier.color}10, rgba(10,25,50,0.55))` }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: '2.5rem' }}>{currentTier.icon}</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 900, color: currentTier.color }}>{currentTier.label} Stufe</div>
                <div style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.72)', marginTop: 4 }}>{monthStats.hardwareCount} Hardware-Verkäufe diesen Monat</div>
                {currentTier.bonus > 0 && (
                  <div style={{ marginTop: 8, padding: '8px 14px', borderRadius: 10, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.2)', display: 'inline-block' }}>
                    <span style={{ fontSize: '0.88rem', fontWeight: 800, color: '#00f5d4' }}>+{fmtEuro(currentTier.bonus)} Bonus</span>
                  </div>
                )}
              </div>
              {nextTier && (
                <div style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.6rem', color: 'rgba(200,220,255,0.65)' }}>Nächste Stufe: <strong style={{ color: nextTier.color }}>{nextTier.icon} {nextTier.label}</strong></div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', marginTop: 4 }}>Noch {nextTier.min - monthStats.hardwareCount} Hardware-Verkäufe</div>
                  <div style={{ width: '100%', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', marginTop: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, (monthStats.hardwareCount / nextTier.min) * 100)}%`, height: '100%', background: `linear-gradient(90deg, ${currentTier.color}, ${nextTier.color})`, borderRadius: 3, transition: 'width 0.5s' }} />
                  </div>
                </div>
              )}
            </GlassCard>

            {/* Incentive Tiers Overview */}
            <GlassCard glow="purple">
              <SectionLabel icon="🏆">Incentive-Stufen</SectionLabel>
              {INCENTIVE_TIERS.map(tier => {
                const isActive = currentTier.label === tier.label;
                return (
                  <div key={tier.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, background: isActive ? `${tier.color}12` : 'rgba(255,255,255,0.02)', border: `1px solid ${isActive ? `${tier.color}33` : 'rgba(255,255,255,0.05)'}`, marginBottom: 6 }}>
                    <div style={{ fontSize: '1.5rem' }}>{tier.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 800, color: isActive ? tier.color : '#fff' }}>{tier.label}</div>
                      <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.6)' }}>{tier.min}–{tier.max === 999 ? '∞' : tier.max} Hardware-Verkäufe</div>
                    </div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 800, color: tier.bonus > 0 ? '#00f5d4' : 'rgba(200,220,255,0.5)' }}>
                      {tier.bonus > 0 ? `+${fmtEuro(tier.bonus)}` : '—'}
                    </div>
                  </div>
                );
              })}
            </GlassCard>
          </div>
        )}
      </div>
    </div>
  );
}

export default AbschlussReport;
